/* eslint-disable react-refresh/only-export-components */
import React from 'react'
import type { LucideIcon } from 'lucide-react'
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Calculator,
  CalendarDays,
  ChartColumn,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Code2,
  Columns3,
  Combine,
  Copy,
  CopyPlus,
  Database,
  Download,
  Eraser,
  Eye,
  EyeOff,
  Filter,
  FunctionSquare,
  Grid3X3,
  Home,
  IndentDecrease,
  IndentIncrease,
  Italic,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  Link,
  Lock,
  Map,
  Maximize2,
  MessageSquare,
  MessageSquareX,
  Minus,
  MoreHorizontal,
  Paintbrush,
  PanelTop,
  Pencil,
  Plus,
  RefreshCw,
  Replace,
  Rows3,
  Rows4,
  Save,
  Scissors,
  Search,
  Settings,
  Share2,
  ShieldAlert,
  Sigma,
  Sparkles,
  Tags,
  Trash2,
  Type,
  Underline,
  Unlink,
  Unlock,
  Undo2,
  Upload,
  Redo2,
  WrapText,
  X,
} from 'lucide-react'
import {
  ExcelAccountingFormatIcon,
  ExcelBorderIcon,
  ExcelCellStylesIcon,
  ExcelClearContentsIcon,
  ExcelClearFormatsIcon,
  ExcelCommaStyleIcon,
  ExcelConditionalFormattingIcon,
  ExcelDecreaseDecimalIcon,
  ExcelDeleteCellsIcon,
  ExcelFillColorIcon,
  ExcelFontColorIcon,
  ExcelFontFamilyIcon,
  ExcelFontSizeIcon,
  ExcelFormatAsTableIcon,
  ExcelFormatCellsIcon,
  ExcelIncreaseDecimalIcon,
  ExcelInsertCellsIcon,
  ExcelMergeCenterIcon,
  ExcelNumberFormatIcon,
  ExcelPercentStyleIcon,
} from '../excel/ExcelRibbonIcons'

export type FuseGridButtonIconProps = React.SVGProps<SVGSVGElement> & {
  size?: number | string
  strokeWidth?: number | string
  accentColor?: string
}

export type FuseGridButtonIconKey =
  | 'accept'
  | 'add'
  | 'add-visualization'
  | 'ai-panel'
  | 'align-center'
  | 'align-left'
  | 'align-right'
  | 'bold'
  | 'calculator'
  | 'calendar'
  | 'chart'
  | 'check-circle'
  | 'chevron-down'
  | 'chevron-left'
  | 'chevron-right'
  | 'chevron-up'
  | 'circle-invalid-data'
  | 'clear-validation-circles'
  | 'clipboard'
  | 'close'
  | 'code'
  | 'columns'
  | 'copy'
  | 'copy-plus'
  | 'database'
  | 'decrease-indent'
  | 'delete-table'
  | 'download'
  | 'eraser'
  | 'excel-accounting-format'
  | 'excel-borders'
  | 'excel-cell-styles'
  | 'excel-clear-contents'
  | 'excel-clear-formats'
  | 'excel-comma-style'
  | 'excel-conditional-formatting'
  | 'excel-decrease-decimal'
  | 'excel-delete-cells'
  | 'excel-fill-color'
  | 'excel-font-color'
  | 'excel-font-family'
  | 'excel-font-size'
  | 'excel-format-as-table'
  | 'excel-format-cells'
  | 'excel-increase-decimal'
  | 'excel-insert-cells'
  | 'excel-merge-center'
  | 'excel-number-format'
  | 'excel-percent-style'
  | 'eye'
  | 'eye-off'
  | 'filter'
  | 'function'
  | 'grid'
  | 'hash'
  | 'home'
  | 'increase-indent'
  | 'insert-sparkline-column'
  | 'insert-sparkline-line'
  | 'insert-sparkline-win-loss'
  | 'insert-table'
  | 'italic'
  | 'layers'
  | 'layout-dashboard'
  | 'layout-grid'
  | 'link'
  | 'lock'
  | 'map'
  | 'maximize'
  | 'message'
  | 'message-x'
  | 'minus'
  | 'more-horizontal'
  | 'open-hyperlink'
  | 'paintbrush'
  | 'panel-top'
  | 'paste'
  | 'paste-formats'
  | 'paste-formulas'
  | 'paste-names'
  | 'paste-transpose'
  | 'paste-values'
  | 'pencil'
  | 'pivot'
  | 'plus'
  | 'record'
  | 'redo'
  | 'refresh'
  | 'remove'
  | 'remove-hyperlink'
  | 'rename-table'
  | 'replace'
  | 'resize-table'
  | 'rows'
  | 'save'
  | 'scissors'
  | 'search'
  | 'settings'
  | 'share'
  | 'sigma'
  | 'sparkles'
  | 'table'
  | 'tag'
  | 'toggle-table-filter-buttons'
  | 'toggle-table-header-row'
  | 'toggle-table-total-row'
  | 'trash'
  | 'type'
  | 'underline'
  | 'undo'
  | 'unlink'
  | 'unlock'
  | 'upload'
  | 'view-normal'
  | 'view-page-break'
  | 'view-page-layout'
  | 'wrap-text'
  | 'zoom-in'
  | 'zoom-out'

type CustomIconProps = FuseGridButtonIconProps & {
  children: React.ReactNode
}

const DEFAULT_ACCENT = 'var(--fg-icon-accent, var(--fg-green, #107c41))'

export function FuseGridButtonIcon({
  size = 16,
  strokeWidth = 2,
  accentColor = DEFAULT_ACCENT,
  children,
  style,
  ...svgProps
}: CustomIconProps): React.ReactElement {
  return (
    <svg
      {...svgProps}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      data-fg-button-icon
      style={{ display: 'block', overflow: 'visible', ...style, '--fg-icon-accent': accentColor } as React.CSSProperties}
    >
      {children}
    </svg>
  )
}

function wrapLucide(Icon: LucideIcon, strokeWidth = 2): React.ElementType<FuseGridButtonIconProps> {
  return function FuseGridLucideIcon({
    size = 16,
    strokeWidth: strokeWidthOverride,
    accentColor: _accentColor,
    style,
    ...props
  }: FuseGridButtonIconProps) {
    return (
      <Icon
        {...props}
        size={size}
        strokeWidth={Number(strokeWidthOverride ?? strokeWidth)}
        aria-hidden="true"
        focusable="false"
        style={{ display: 'block', ...style }}
      />
    )
  }
}

export function PasteIcon(props: FuseGridButtonIconProps) {
  return (
    <FuseGridButtonIcon {...props}>
      <path d="M9 4.5h6l1 2h2.2A1.8 1.8 0 0 1 20 8.3v10.4a1.8 1.8 0 0 1-1.8 1.8H5.8A1.8 1.8 0 0 1 4 18.7V8.3a1.8 1.8 0 0 1 1.8-1.8H8l1-2Z" />
      <path d="M8.8 6.5h6.4" />
      <rect x="7.2" y="10" width="9.6" height="7" rx="1.4" fill="var(--fg-icon-accent)" stroke="none" opacity="0.16" />
      <path d="M8.6 12h6.8M8.6 15h4.8" />
    </FuseGridButtonIcon>
  )
}

export function PasteValuesIcon(props: FuseGridButtonIconProps) {
  return (
    <FuseGridButtonIcon {...props}>
      <path d="M9 4.5h6l1 2h2.2A1.8 1.8 0 0 1 20 8.3v10.4a1.8 1.8 0 0 1-1.8 1.8H5.8A1.8 1.8 0 0 1 4 18.7V8.3a1.8 1.8 0 0 1 1.8-1.8H8l1-2Z" />
      <path d="M8.8 6.5h6.4" />
      <text x="12" y="16" textAnchor="middle" fontSize="6.4" fill="currentColor" stroke="none" fontWeight="800">123</text>
      <rect x="7" y="10" width="10" height="7.5" rx="1.4" fill="var(--fg-icon-accent)" stroke="none" opacity="0.13" />
    </FuseGridButtonIcon>
  )
}

export function PasteFormulasIcon(props: FuseGridButtonIconProps) {
  return (
    <FuseGridButtonIcon {...props}>
      <path d="M9 4.5h6l1 2h2.2A1.8 1.8 0 0 1 20 8.3v10.4a1.8 1.8 0 0 1-1.8 1.8H5.8A1.8 1.8 0 0 1 4 18.7V8.3a1.8 1.8 0 0 1 1.8-1.8H8l1-2Z" />
      <path d="M8.8 6.5h6.4" />
      <path d="M8 15.5h2.1l2.2-5 1.5 3.4h2.8" />
      <path d="M7.8 10.5h8.4" stroke="var(--fg-icon-accent)" />
    </FuseGridButtonIcon>
  )
}

export function PasteFormatsIcon(props: FuseGridButtonIconProps) {
  return (
    <FuseGridButtonIcon {...props}>
      <path d="M9 4.5h6l1 2h2.2A1.8 1.8 0 0 1 20 8.3v5.4" />
      <path d="M8.8 6.5h6.4" />
      <path d="M4 8.3v10.4a1.8 1.8 0 0 0 1.8 1.8h6.2" />
      <path d="M8 12h5.7M8 15h3.2" />
      <path d="M14.2 18.7l4.9-4.9 1.7 1.7-4.9 4.9h-1.7v-1.7Z" fill="var(--fg-icon-accent)" fillOpacity="0.14" />
      <path d="M14.2 18.7l4.9-4.9 1.7 1.7-4.9 4.9h-1.7v-1.7Z" />
    </FuseGridButtonIcon>
  )
}

export function PasteTransposeIcon(props: FuseGridButtonIconProps) {
  return (
    <FuseGridButtonIcon {...props}>
      <path d="M5.5 5.5h7v7h-7zM12.5 11.5h6v7h-6z" fill="var(--fg-icon-accent)" stroke="none" opacity="0.13" />
      <path d="M5.5 5.5h7v7h-7zM12.5 11.5h6v7h-6z" />
      <path d="M7.2 9h3.6M15.5 15h.1M15.5 12.5v6" />
      <path d="M15.5 7.2h2.7v2.7M18.2 7.2l-5.1 5.1" />
    </FuseGridButtonIcon>
  )
}

export function PasteNamesIcon(props: FuseGridButtonIconProps) {
  return (
    <FuseGridButtonIcon {...props}>
      <path d="M9 4.5h6l1 2h2.2A1.8 1.8 0 0 1 20 8.3v10.4a1.8 1.8 0 0 1-1.8 1.8H5.8A1.8 1.8 0 0 1 4 18.7V8.3a1.8 1.8 0 0 1 1.8-1.8H8l1-2Z" />
      <path d="M8.8 6.5h6.4" />
      <path d="M7.5 12.2h4M7.5 15.2h4" />
      <path d="M14.2 11.5h2.1l1.2 1.2v3.9h-3.3z" fill="var(--fg-icon-accent)" fillOpacity="0.14" />
      <path d="M14.2 11.5h2.1l1.2 1.2v3.9h-3.3z" />
    </FuseGridButtonIcon>
  )
}

export function TableIcon(props: FuseGridButtonIconProps) {
  return (
    <FuseGridButtonIcon {...props}>
      <rect x="4.5" y="5" width="15" height="14" rx="1.7" />
      <path d="M4.5 9.5h15" stroke="var(--fg-icon-accent)" />
      <path d="M4.5 14h15M9.5 5v14M14.5 5v14" opacity="0.75" />
      <rect x="5.5" y="6" width="13" height="2.7" rx="0.8" fill="var(--fg-icon-accent)" stroke="none" opacity="0.16" />
    </FuseGridButtonIcon>
  )
}

export function ResizeTableIcon(props: FuseGridButtonIconProps) {
  return (
    <FuseGridButtonIcon {...props}>
      <rect x="4.5" y="5" width="11.5" height="11.5" rx="1.5" />
      <path d="M4.5 9h11.5M8.3 5v11.5M12.1 5v11.5" opacity="0.65" />
      <path d="M14.8 19.5h4.7v-4.7" stroke="var(--fg-icon-accent)" />
      <path d="M19.5 19.5l-5.2-5.2" />
    </FuseGridButtonIcon>
  )
}

export function RenameTableIcon(props: FuseGridButtonIconProps) {
  return (
    <FuseGridButtonIcon {...props}>
      <rect x="4.5" y="5" width="12" height="12" rx="1.5" />
      <path d="M4.5 9h12M8.5 5v12M12.5 5v12" opacity="0.55" />
      <path d="M14.5 19.2l4.6-4.6 1.3 1.3-4.6 4.6h-1.3v-1.3Z" fill="var(--fg-icon-accent)" fillOpacity="0.16" />
      <path d="M14.5 19.2l4.6-4.6 1.3 1.3-4.6 4.6h-1.3v-1.3Z" />
    </FuseGridButtonIcon>
  )
}

export function HeaderRowIcon(props: FuseGridButtonIconProps) {
  return (
    <FuseGridButtonIcon {...props}>
      <rect x="4.5" y="5" width="15" height="14" rx="1.7" />
      <path d="M4.5 9.5h15M9.5 5v14M14.5 5v14M4.5 14h15" opacity="0.55" />
      <rect x="5.7" y="6.2" width="12.6" height="2.2" rx="0.6" fill="var(--fg-icon-accent)" stroke="none" opacity="0.36" />
    </FuseGridButtonIcon>
  )
}

export function TotalRowIcon(props: FuseGridButtonIconProps) {
  return (
    <FuseGridButtonIcon {...props}>
      <rect x="4.5" y="5" width="15" height="14" rx="1.7" />
      <path d="M4.5 9.5h15M9.5 5v14M14.5 5v14M4.5 14h15" opacity="0.55" />
      <rect x="5.7" y="15.6" width="12.6" height="2.2" rx="0.6" fill="var(--fg-icon-accent)" stroke="none" opacity="0.36" />
    </FuseGridButtonIcon>
  )
}

export function FilterButtonsIcon(props: FuseGridButtonIconProps) {
  return (
    <FuseGridButtonIcon {...props}>
      <rect x="4.5" y="5" width="15" height="14" rx="1.7" />
      <path d="M4.5 9.5h15M9.5 5v14M14.5 5v14M4.5 14h15" opacity="0.45" />
      <path d="M7.2 7.4h3.4M8.9 7.4v2.1M13.3 7.4h3.4M15 7.4v2.1" stroke="var(--fg-icon-accent)" />
    </FuseGridButtonIcon>
  )
}

export function SparklineLineIcon(props: FuseGridButtonIconProps) {
  return (
    <FuseGridButtonIcon {...props}>
      <rect x="4.5" y="6" width="15" height="12" rx="1.8" />
      <path d="M7 15.2l3-3 2.4 1.8 4.6-5" stroke="var(--fg-icon-accent)" strokeWidth="2.3" />
      <path d="M7 18h10" opacity="0.4" />
    </FuseGridButtonIcon>
  )
}

export function SparklineColumnIcon(props: FuseGridButtonIconProps) {
  return (
    <FuseGridButtonIcon {...props}>
      <rect x="4.5" y="6" width="15" height="12" rx="1.8" />
      <path d="M8 15v-3M12 15V9M16 15v-5" stroke="var(--fg-icon-accent)" strokeWidth="2.3" />
      <path d="M7 15.5h10" opacity="0.5" />
    </FuseGridButtonIcon>
  )
}

export function SparklineWinLossIcon(props: FuseGridButtonIconProps) {
  return (
    <FuseGridButtonIcon {...props}>
      <rect x="4.5" y="6" width="15" height="12" rx="1.8" />
      <path d="M7 12h10" opacity="0.5" />
      <path d="M8 9.5h2.2v2.5H8zM11 12h2.2v2.5H11zM14 8.5h2.2V12H14z" fill="var(--fg-icon-accent)" stroke="none" opacity="0.75" />
    </FuseGridButtonIcon>
  )
}

export function InvalidDataIcon(props: FuseGridButtonIconProps) {
  return (
    <FuseGridButtonIcon {...props}>
      <rect x="4.5" y="5.5" width="15" height="13" rx="1.8" />
      <path d="M4.5 10h15M9.5 5.5v13M14.5 5.5v13" opacity="0.38" />
      <circle cx="15.8" cy="13.8" r="3.2" fill="var(--fg-icon-accent)" stroke="none" opacity="0.18" />
      <path d="M15.8 11.9v2.1M15.8 16h.01" stroke="var(--fg-icon-accent)" strokeWidth="2.2" />
    </FuseGridButtonIcon>
  )
}

export function AddVizIcon(props: FuseGridButtonIconProps) {
  return (
    <FuseGridButtonIcon {...props}>
      <rect x="4.5" y="5.5" width="7" height="6" rx="1.4" />
      <rect x="4.5" y="14" width="7" height="4.5" rx="1.4" />
      <rect x="14" y="5.5" width="5.5" height="5.5" rx="1.4" fill="var(--fg-icon-accent)" stroke="none" opacity="0.18" />
      <path d="M16.8 5.5v5.5M14 8.3h5.5" stroke="var(--fg-icon-accent)" />
      <path d="M14 14h5.5v4.5H14z" />
    </FuseGridButtonIcon>
  )
}

export function DefineNameIcon(props: FuseGridButtonIconProps) {
  return (
    <FuseGridButtonIcon {...props}>
      <path d="M5.5 6.5h8.3l3 3v8A1.5 1.5 0 0 1 15.3 19h-9.8A1.5 1.5 0 0 1 4 17.5V8a1.5 1.5 0 0 1 1.5-1.5Z" />
      <path d="M13.8 6.7v3h3" />
      <path d="M7.3 13.1h5.2M7.3 16h3.5" />
      <path d="M17.8 13.4v5M15.3 15.9h5" stroke="var(--fg-icon-accent)" strokeWidth="2.2" />
    </FuseGridButtonIcon>
  )
}

export function ZoomOutIcon(props: FuseGridButtonIconProps) {
  return (
    <FuseGridButtonIcon {...props}>
      <circle cx="10.5" cy="10.5" r="5.6" />
      <path d="M14.6 14.6l4.4 4.4" />
      <path d="M8.2 10.5h4.6" stroke="var(--fg-icon-accent)" strokeWidth="2.3" />
    </FuseGridButtonIcon>
  )
}

export function ZoomInIconFg(props: FuseGridButtonIconProps) {
  return (
    <FuseGridButtonIcon {...props}>
      <circle cx="10.5" cy="10.5" r="5.6" />
      <path d="M14.6 14.6l4.4 4.4" />
      <path d="M10.5 8.2v4.6M8.2 10.5h4.6" stroke="var(--fg-icon-accent)" strokeWidth="2.3" />
    </FuseGridButtonIcon>
  )
}

export const FUSEGRID_BUTTON_ICON_REGISTRY: Record<FuseGridButtonIconKey, React.ElementType<FuseGridButtonIconProps>> = {
  accept: wrapLucide(Check),
  add: wrapLucide(Plus),
  'add-visualization': AddVizIcon,
  'ai-panel': wrapLucide(Sparkles),
  'align-center': wrapLucide(AlignCenter),
  'align-left': wrapLucide(AlignLeft),
  'align-right': wrapLucide(AlignRight),
  bold: wrapLucide(Bold),
  calculator: wrapLucide(Calculator),
  calendar: wrapLucide(CalendarDays),
  chart: wrapLucide(ChartColumn),
  'check-circle': wrapLucide(CheckCircle2),
  'chevron-down': wrapLucide(ChevronDown),
  'chevron-left': wrapLucide(ChevronLeft),
  'chevron-right': wrapLucide(ChevronRight),
  'chevron-up': wrapLucide(ChevronUp),
  'circle-invalid-data': InvalidDataIcon,
  'clear-validation-circles': wrapLucide(ShieldAlert),
  clipboard: PasteIcon,
  close: wrapLucide(X, 2.3),
  code: wrapLucide(Code2),
  columns: wrapLucide(Columns3),
  copy: wrapLucide(Copy),
  'copy-plus': wrapLucide(CopyPlus),
  database: wrapLucide(Database),
  'decrease-indent': wrapLucide(IndentDecrease),
  'delete-table': wrapLucide(Trash2),
  download: wrapLucide(Download),
  eraser: wrapLucide(Eraser),
  'excel-accounting-format': ExcelAccountingFormatIcon,
  'excel-borders': ExcelBorderIcon,
  'excel-cell-styles': ExcelCellStylesIcon,
  'excel-clear-contents': ExcelClearContentsIcon,
  'excel-clear-formats': ExcelClearFormatsIcon,
  'excel-comma-style': ExcelCommaStyleIcon,
  'excel-conditional-formatting': ExcelConditionalFormattingIcon,
  'excel-decrease-decimal': ExcelDecreaseDecimalIcon,
  'excel-delete-cells': ExcelDeleteCellsIcon,
  'excel-fill-color': ExcelFillColorIcon,
  'excel-font-color': ExcelFontColorIcon,
  'excel-font-family': ExcelFontFamilyIcon,
  'excel-font-size': ExcelFontSizeIcon,
  'excel-format-as-table': ExcelFormatAsTableIcon,
  'excel-format-cells': ExcelFormatCellsIcon,
  'excel-increase-decimal': ExcelIncreaseDecimalIcon,
  'excel-insert-cells': ExcelInsertCellsIcon,
  'excel-merge-center': ExcelMergeCenterIcon,
  'excel-number-format': ExcelNumberFormatIcon,
  'excel-percent-style': ExcelPercentStyleIcon,
  eye: wrapLucide(Eye),
  'eye-off': wrapLucide(EyeOff),
  filter: wrapLucide(Filter),
  function: wrapLucide(FunctionSquare),
  grid: wrapLucide(Grid3X3),
  hash: wrapLucide(Type),
  home: wrapLucide(Home),
  'increase-indent': wrapLucide(IndentIncrease),
  'insert-sparkline-column': SparklineColumnIcon,
  'insert-sparkline-line': SparklineLineIcon,
  'insert-sparkline-win-loss': SparklineWinLossIcon,
  'insert-table': TableIcon,
  italic: wrapLucide(Italic),
  layers: wrapLucide(Layers),
  'layout-dashboard': wrapLucide(LayoutDashboard),
  'layout-grid': wrapLucide(LayoutGrid),
  link: wrapLucide(Link),
  lock: wrapLucide(Lock),
  map: wrapLucide(Map),
  maximize: wrapLucide(Maximize2),
  message: wrapLucide(MessageSquare),
  'message-x': wrapLucide(MessageSquareX),
  minus: wrapLucide(Minus),
  'more-horizontal': wrapLucide(MoreHorizontal),
  'open-hyperlink': wrapLucide(Link),
  paintbrush: wrapLucide(Paintbrush),
  'panel-top': wrapLucide(PanelTop),
  paste: PasteIcon,
  'paste-formats': PasteFormatsIcon,
  'paste-formulas': PasteFormulasIcon,
  'paste-names': PasteNamesIcon,
  'paste-transpose': PasteTransposeIcon,
  'paste-values': PasteValuesIcon,
  pencil: wrapLucide(Pencil),
  pivot: wrapLucide(Combine),
  plus: DefineNameIcon,
  record: wrapLucide(Sparkles),
  redo: wrapLucide(Redo2),
  refresh: wrapLucide(RefreshCw),
  remove: wrapLucide(X, 2.4),
  'remove-hyperlink': wrapLucide(Unlink),
  'rename-table': RenameTableIcon,
  replace: wrapLucide(Replace),
  'resize-table': ResizeTableIcon,
  rows: wrapLucide(Rows4),
  save: wrapLucide(Save),
  scissors: wrapLucide(Scissors),
  search: wrapLucide(Search),
  settings: wrapLucide(Settings),
  share: wrapLucide(Share2),
  sigma: wrapLucide(Sigma),
  sparkles: wrapLucide(Sparkles),
  table: TableIcon,
  tag: wrapLucide(Tags),
  'toggle-table-filter-buttons': FilterButtonsIcon,
  'toggle-table-header-row': HeaderRowIcon,
  'toggle-table-total-row': TotalRowIcon,
  trash: wrapLucide(Trash2),
  type: wrapLucide(Type),
  underline: wrapLucide(Underline),
  undo: wrapLucide(Undo2),
  unlink: wrapLucide(Unlink),
  unlock: wrapLucide(Unlock),
  upload: wrapLucide(Upload),
  'view-normal': wrapLucide(Grid3X3),
  'view-page-break': wrapLucide(Rows3),
  'view-page-layout': wrapLucide(LayoutGrid),
  'wrap-text': wrapLucide(WrapText),
  'zoom-in': ZoomInIconFg,
  'zoom-out': ZoomOutIcon,
}

export function getFuseGridButtonIcon(iconKey: string): React.ElementType<FuseGridButtonIconProps> | undefined {
  return FUSEGRID_BUTTON_ICON_REGISTRY[iconKey as FuseGridButtonIconKey]
}

export function hasFuseGridButtonIcon(iconKey: string): iconKey is FuseGridButtonIconKey {
  return iconKey in FUSEGRID_BUTTON_ICON_REGISTRY
}
